import { motion } from 'framer-motion';
import { format } from 'date-fns/format';
import { Check, CheckCheck, AlertCircle } from 'lucide-react';
import type { Message } from '@/types/api';
import { cn } from '@/lib/utils';
import { bubbleVariants } from '../lib/motion';

export const MessageBubble = ({ message }: { message: Message }) => {
  const isOutbound = message.direction === 'outbound';

  return (
    <motion.div
      variants={bubbleVariants(isOutbound)}
      className={cn('flex', isOutbound ? 'justify-end' : 'justify-start')}
    >
      <div
        className={cn(
          'max-w-sm rounded-2xl px-3.5 py-2.5 lg:max-w-md shadow-2xs text-xs leading-relaxed',
          isOutbound
            ? 'bg-slate-900 dark:bg-primary text-white rounded-br-xs'
            : 'border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-850 text-slate-800 dark:text-slate-200 rounded-bl-xs'
        )}
      >
        {message.mediaUrl && (
          <a href={message.mediaUrl} target="_blank" rel="noopener noreferrer" className="block">
            <img
              src={message.mediaUrl}
              alt={message.content || 'Sent image'}
              loading="lazy"
              className="mb-1.5 max-h-60 w-full rounded-xl object-cover"
            />
          </a>
        )}
        {message.content && <p className="whitespace-pre-wrap">{message.content}</p>}
        <div
          className={cn(
            'mt-1 flex items-center justify-end gap-1 text-[10px]',
            isOutbound ? 'text-slate-400 dark:text-primary-foreground/70' : 'text-slate-400 dark:text-slate-500'
          )}
        >
          <span className="tabular-nums">{format(new Date(message.timestamp), 'HH:mm')}</span>
          {isOutbound && (
            <>
              {message.status === 'sent' && <Check className="h-3 w-3" />}
              {message.status === 'delivered' && <CheckCheck className="h-3 w-3" />}
              {message.status === 'read' && <CheckCheck className="h-3 w-3 text-sky-400" />}
              {message.status === 'failed' && <AlertCircle className="h-3 w-3 text-rose-400" />}
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};
